import { useEffect, useRef } from "react";
import { triggerAI, setAIStatus, getLatestInsights, setInsightDisplayed } from "../lib/api";
import { isVisibleResponse } from "../lib/responses";
import type { EventTool, Question, Response } from "../lib/types";

// Initial cluster fires when we first cross this many visible responses.
// Subsequent re-clusters are gated on new submissions since the last attempt
// (Stage Calm: real new participation, not transient cluster_id IS NULL counts).
const INITIAL_CLUSTER_THRESHOLD = 5;

interface Props {
  question: Question | null;
  responses: Response[];
  tools: EventTool[];
  accessToken: string;
  eventId: string;
  /** Re-cluster fires when this many NEW visible responses have arrived since the last cluster attempt. */
  reclusterThreshold?: number;
  /** Insight fan-out fires when this many NEW visible responses have arrived since the last insight attempt. */
  insightTriggerThreshold?: number;
}

export function useSurveyAI({
  question,
  responses,
  tools,
  accessToken,
  eventId,
  reclusterThreshold = 5,
  insightTriggerThreshold = 10,
}: Props) {
  // Anchored to the visible-response count at the moment of the *last attempted*
  // run, not to the count after a successful write. This way a server-side skip
  // (e.g. cadence gate, validation rejection) still moves the anchor forward
  // and we don't spam the same threshold over and over.
  const lastClusterCountRef = useRef<number>(0);
  const lastInsightCountRef = useRef<number>(0);
  const runningRef = useRef<boolean>(false);
  // Set once per question after we've observed that an initial cluster run
  // already exists for this question. Used to skip the very first tick after
  // mount when prior state would otherwise look like "thresholds crossed"
  // and spuriously fire a cluster + insight fan-out. Mirrors the snapshot
  // pattern in useAutoCluster (lines 53-59 there).
  const hydratedRef = useRef<boolean>(false);

  const visibleResponses = responses.filter((r) => isVisibleResponse(r));

  useEffect(() => {
    if (!question) return;
    if (runningRef.current) return;

    // Anon viewers (public stage on a venue projector, embedded preview, etc.)
    // can't authenticate to the edge function. Skip the fan-out entirely so we
    // don't flood the console with 401s — only authenticated browsers drive
    // AI for survey-mode events today.
    if (!accessToken) return;

    const count = visibleResponses.length;
    if (count === 0) return;

    const hasInitialRun =
      visibleResponses.some((r) => r.cluster_id != null) || lastClusterCountRef.current > 0;

    // Hydration: on the first tick where we can see an initial cluster run
    // already happened for this question (because responses arrive with
    // cluster_id set), snapshot both anchors to the current count and bail.
    // Without this, refs initialised to 0 make N responses look like N new
    // submissions since the last run on stage reload — firing a spurious
    // recluster and 3 wasted insight calls. The next render proceeds with
    // hydrated anchors and normal threshold math.
    if (!hydratedRef.current && hasInitialRun) {
      lastClusterCountRef.current = count;
      lastInsightCountRef.current = count;
      hydratedRef.current = true;
      return;
    }

    const isFrozen = !!question.is_frozen;

    const newSinceLastClusterRun = count - lastClusterCountRef.current;
    const newSinceLastInsightRun = count - lastInsightCountRef.current;

    // Initial cluster: fires once when we reach INITIAL_CLUSTER_THRESHOLD visible
    // responses and no cluster_run has ever happened.
    const initialDue = !hasInitialRun && count >= INITIAL_CLUSTER_THRESHOLD;
    // Re-cluster: real new submissions since the last attempt cross the threshold,
    // and the question is not frozen.
    const reclusterDue =
      hasInitialRun && !isFrozen && newSinceLastClusterRun >= reclusterThreshold;
    const clusterDue = initialDue || reclusterDue;
    // Insight fan-out: real new submissions since the last attempt cross the
    // separate insight threshold. Independent of cluster cadence.
    const insightDue = newSinceLastInsightRun >= insightTriggerThreshold;

    const enabledInsightTools = tools.filter((t) => t.is_enabled);
    // Nothing to do this tick: no cluster owed and no insight owed.
    if (!clusterDue && (!insightDue || enabledInsightTools.length === 0)) return;

    const run = async () => {
      runningRef.current = true;
      try {
        if (clusterDue) {
          await setAIStatus(eventId, "cluster", "Cluster werden gebildet…");
          await triggerAI({
            type: "cluster",
            questionId: question.id,
            accessToken,
            triggerReason: initialDue ? "initial" : "auto_threshold",
          });
          lastClusterCountRef.current = count;
        }

        // Stage Calm: the entire insight fan-out is gated on insightDue. When
        // false we don't even write ai_status — the room stays calm.
        if (insightDue && enabledInsightTools.length > 0) {
          let anyRan = false;
          for (const tool of enabledInsightTools) {
            await setAIStatus(eventId, tool.tool_key, `${tool.label} wird ausgeführt…`);
            const result = await triggerAI({
              type: "insight",
              toolId: tool.id,
              questionId: question.id,
              accessToken,
            });
            if (!result.skipped) anyRan = true;
          }
          // Stage Calm: anchor cadence to ATTEMPTS, not to server-side runs. A
          // skipped run still moves the anchor forward.
          lastInsightCountRef.current = count;

          // Auto-display anything that's not currently displayed (in case the
          // facilitator hid an earlier result and a fresh one just landed).
          if (anyRan) {
            const insights = await getLatestInsights(question.id);
            await Promise.all(
              insights.filter((i) => !i.is_displayed).map((i) => setInsightDisplayed(i.id, true)),
            );
          }
        }
      } catch (err) {
        console.error("[useSurveyAI] AI run failed", err);
      } finally {
        await setAIStatus(eventId, null, null);
        runningRef.current = false;
      }
    };

    run();
    // Stage Calm: depend on visibleResponses.length only — uncategorizedCount
    // is removed because it churns during re-cluster windows. New submissions
    // are the only trigger source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visibleResponses.length,
    question?.id,
    tools.length,
    reclusterThreshold,
    insightTriggerThreshold,
  ]);

  // Reset cadence anchors when the question changes.
  useEffect(() => {
    lastClusterCountRef.current = 0;
    lastInsightCountRef.current = 0;
    runningRef.current = false;
    hydratedRef.current = false;
  }, [question?.id]);
}
